// angular
import { Component, ElementRef, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { map } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
// common
import { IQuestion, TreeItemType, AdviceDoc } from '@aa/common';

// shared
import { AdviceState, QnaStage, SelfServAnalyticsService, TreeQuestion, TreeQuestionResponse } from '@aa/selfserv/shared';

import { AbstractQnaComponent } from '../abstract-qna.component';
import { QnaNavigationService } from '../qna-navigation.service';


/**
 * See here for style info:
 * https://design-system.service.gov.uk/
 */
@Component({
  selector: 'aa-questions',
  templateUrl: './questions.component.html',
  styleUrls: ['./questions.component.scss']
})
export class QnaQuestionsComponent extends AbstractQnaComponent {

  constructor(navigation: QnaNavigationService,
              route: ActivatedRoute,
              private readonly analytics: SelfServAnalyticsService) {
    super(navigation, 'Questions', QnaStage.Questions);

    const question$ = route.paramMap.pipe(map(params => this.parseQuestion(params)));
    const path$ = route.paramMap.pipe(map(QnaQuestionsComponent.parsePath));

    super.subscribe(combineLatest([question$, path$]), ([question, path]) => {
      this.init(question, path);
    });
  }

  private rootQuestion: IQuestion = null;
  public popularTopics: TreeQuestionResponse[] = null;
  public treePath: TreeQuestion[] = [];
  public visibleResponses: TreeQuestionResponse[] = [];

  public initialized: boolean = false;
  public error: string;

  private path: number[] = [];
  public result: AdviceDoc = null;

  @ViewChild('adviceButton') adviceButtonRef: ElementRef;
  @ViewChildren('treeItem') treeElements: QueryList<ElementRef>;

  public adviceHref: string;
  public externalHostName: string;

  // route link state param
  public readonly adviceState: AdviceState = { questionsComplete: true };

  // Initial question index
  public q0: number = -1;

  private static parsePath(params: ParamMap): number[] {

    const path = params.get('path');

    if (!path) {
      return [];
    }

    return path
      .split(',')
      .map( o => +o); // convert to number
  }

  private parseQuestion(params: ParamMap): IQuestion {
    const questionId = params.get('questionId');

    return this.data.questions.find(o => o.slug === questionId || o.id === questionId);
  }

  public adviceClick() {
    this.analytics.trackQuestionsComplete();
  }

  public init(question: IQuestion, path: number[]) {
    if (!question) {
      question = this.data.questions[0];
    }

    this.analytics.trackQuestionsStart(question, path);

    this.rootQuestion = question;
    this.path = [];
    this.treePath = [];
    this.initialized = true;
    this.adviceHref = null;
    this.externalHostName = null;
    this.error = null;
    this.visibleResponses = [];
    this.popularTopics = null;

    if (!this.pushQuestion(question)) {
      return;
    }

    for (let i = 0; i < path.length; i++) {
      if (!this.appendResponse(path[i])) {
        break;
      }
    }

    // process hrefs for last question only
    const count = this.treePath.length;
    const lastQuestion: TreeQuestion = this.treePath[count - 1];

    for (let i = 0 ; i < lastQuestion.responses.length; i++) {
      this.setQuestionResponseHref(lastQuestion, i);
    }

    // get visible responses
    this.visibleResponses = lastQuestion.responses.filter(o => !o.hidden);

    if (this.adviceHref) {
      this.navigation.setCurrentSubPage('Questions Complete');
    } else {
      this.navigation.setCurrentSubPage(lastQuestion.text);
    }

    // popular topics based on last question
    this.popularTopics = this.processRelatedQuestion(lastQuestion.relatedQuestionId);

    setTimeout(() => this.initView());
  }

  private processRelatedQuestion(id: Guid): TreeQuestionResponse[] {
    const relatedQuestion = id && this.data.questions.find(o => o.id === id);
    if (!relatedQuestion) {
      return;
    }

    const url = `/questions/${relatedQuestion.slug}`;

    const result = [];
    for (let i = 0 ; i < relatedQuestion.responses.length; i++) {
      const item = new TreeQuestionResponse(relatedQuestion.responses[i]);
      item.responseHref = `${url}/${i}`;
      result.push(item);
    }
    return result;
  }

  private getHostNameFromUrl(url: string) {
    if (!url) {
      return null;
    }

    // chop off the protocol
    const parts = url.split('//');
    if (parts.length < 2) {
      return null;
    }

    // now split on first slash
    return parts[1].split('/')[0];
  }

  private appendResponse(index: number): boolean {

    const length = this.treePath.length;

    // can't append response if no questions defined
    if (length === 0) {
      return false;
    }

    const lastItem = this.treePath[length - 1];

    if (!lastItem.isValidIndex(index)) {
      return false;
    }

    const response = lastItem.responses[index];

    // push index to path before rendering any child links
    this.path.push(index);

    // advice set
    switch (response.nextType) {
      case TreeItemType.Link:
        this.externalHostName = this.getHostNameFromUrl(response.href);

        if (this.externalHostName) {
          this.adviceHref = response.href;
        } else {
          this.setError('Invalid advice link');
        }

        break;

      case TreeItemType.MoreInfo:
        lastItem.showInfo = true;
        break;

      case TreeItemType.Variable:
        response.inline = true;
        lastItem.noHighlight = true;
        this.path.pop();
        return false; // halt tree building

      case TreeItemType.AdviceSet:
        this.adviceHref = this.navigation.getAdviceSetHrefById(response.nextId);
        lastItem.setSelectedIndex(index);

        if (!this.adviceHref) {
          this.setError('Failed to find your advice');
        }

        break;

      case TreeItemType.Question:
      default:
        const nextQuestion = this.data.getQuestion(response.nextId);
        lastItem.setSelectedIndex(index);

        if (nextQuestion) {
          this.pushQuestion(nextQuestion);
        } else {
          this.setError('This topic is still under construction');
        }
        break;
    }


    return true; // carry on building the tree
  }

  private pushQuestion(q: IQuestion): boolean {
    if (!q) {
      // TODO: render results page
      return false;
    }

    const treeQuestion = new TreeQuestion(q);
    treeQuestion.questionHref = this.renderQuestionHref(this.path);

    this.treePath.push(treeQuestion);
    return true;
  }

  private setQuestionResponseHref(question: TreeQuestion, index: number): string {

    const response = question.responses[index];

    if (!response) {
      return;
    }

    const id = response.nextId;

    // validate the id
    switch (response.nextType) {

      case TreeItemType.Link:
        if (!response.href) {
          response.text += ' (invalid link)';
          return;
        }
        break;

      case TreeItemType.AdviceSet:
        if (!this.data.getAdviceSet(id)) {
          response.text += ' (under construction)';
          return;
        }
        break;

      case TreeItemType.MoreInfo:
        if (!question.info || question.showInfo) {
          response.hidden = true;
          return;
        }
        break;

      case TreeItemType.Variable:
        response.inlineResponseText = this.renderInlineResponseText(response);
        response.hidden = !response.inlineResponseText; // only show response if response data available
        break;

      case TreeItemType.Question:
      default:
        if (!this.data.getQuestion(id)) {
          response.text += ' (under construction)';
          return;
        }
        break;
    }

    const responseLinkPath = this.path.slice();
    responseLinkPath.push(index);

    response.responseHref = this.renderQuestionHref(responseLinkPath);
  }

  private renderQuestionHref(path: number[]) {

    const url = '/questions';

    if (!this.rootQuestion) {
      return url;
    }


    const questionId = this.rootQuestion.slug || this.rootQuestion.id;
    const pathStr = QnaNavigationService.serializePath(path);

    return url + `/${questionId}/${pathStr}`;
  }

  private setError(error: string) {
    this.error = error;

    this.analytics.trackQuestionsError(error);
  }

  private initView() {
    // this.focusFirstResponse();
    this.focusAdviceButton();
    this.scrollToLastTreeElement();
  }

  private focusAdviceButton() {
    const el: ElementRef = this.adviceButtonRef;
    if (!el) {
      return;
    }

    el.nativeElement.focus();
  }

  private scrollToLastTreeElement() {
    if (!this.treeElements) {
      return;
    }

    const el: ElementRef = this.treeElements.last;
    if (!el) {
      console.warn('no tree elements found', this.treeElements);
      return;
    }

    const rect = el.nativeElement.getBoundingClientRect();

    const windowHeight = window.innerHeight;
    const pageY = window.pageYOffset;
    const top = rect.top - 20;
    const bottom = rect.bottom + 20;

    let scrollDelta = 0;

    if (top < 0) {
      scrollDelta = top;
    } else if (bottom > windowHeight) {
      // ensure we don't scroll beyond the the top of the element
      scrollDelta = Math.min(bottom - windowHeight, top);
    }

    if (scrollDelta !== 0) {
      const scrollTop = Math.round(pageY + scrollDelta);
      window.scrollTo({top: scrollTop, behavior: 'smooth'});
    }
  }

  renderInlineResponseText(response: TreeQuestionResponse) {
    if (response.nextType !== TreeItemType.Variable) {
      console.warn('no inline response available');
      return null;
    }

    const variable = this.data.getVariable(response.nextId);
    if (!variable) {
      console.warn('Failed to resolve variable', response.nextId);
      return null;
    }

    return variable.value;
  }
}
